/// <reference types="vite/client" />
/**
 * The Hunspell dictionaries the spell check can load, as separately fetched
 * assets: `?url&no-inline` makes the bundler emit each file next to the
 * bundle (never inlined into a JS chunk, however small) and hand back its URL.
 * The worker fetches a language's files the first time a document needs it.
 *
 * The packages export only their Node entry (which reads the files with
 * `node:fs`), not the files themselves, so they are reached by path. Each
 * dictionary ships its licence file beside it: `dictionary-de` is
 * GPL-2.0 OR GPL-3.0 (igerman98), `dictionary-en` is MIT AND BSD (SCOWL).
 * The packages name it `license` without an extension; `licenses/` holds
 * byte copies as `.txt`, so the emitted file is readable (a test keeps them
 * identical to the installed packages).
 */
import deAff from '../../../../node_modules/dictionary-de/index.aff?url&no-inline';
import deDic from '../../../../node_modules/dictionary-de/index.dic?url&no-inline';
import deLicense from './licenses/dictionary-de.txt?url&no-inline';
import enAff from '../../../../node_modules/dictionary-en/index.aff?url&no-inline';
import enDic from '../../../../node_modules/dictionary-en/index.dic?url&no-inline';
import enLicense from './licenses/dictionary-en.txt?url&no-inline';
import type { SpellDictionarySource } from './protocol';

export interface SpellDictionaryAsset {
  readonly aff: string;
  readonly dic: string;
  /** The dictionary's licence text, shipped next to it. */
  readonly license: string;
}

/** One dictionary per supported language: de → de_DE (igerman98), en → en_US (SCOWL). */
export const SPELL_DICTIONARIES: Readonly<Record<'de' | 'en', SpellDictionaryAsset>> = {
  de: { aff: deAff, dic: deDic, license: deLicense },
  en: { aff: enAff, dic: enDic, license: enLicense },
};

/** The dictionary language for a locale or language tag (`de-AT` → `de`); `undefined` when none fits. */
export function spellLanguage(locale: string | undefined): 'de' | 'en' | undefined {
  const primary = locale?.trim().toLowerCase().split(/[-_]/)[0];
  return primary === 'de' || primary === 'en' ? primary : undefined;
}

/**
 * The worker's source for a language. The URLs are made absolute against the
 * page: a worker resolves relative URLs against its own script, not the page.
 */
export function spellDictionarySource(
  language: 'de' | 'en',
  base: string = globalThis.location?.href ?? 'http://localhost/',
): SpellDictionarySource {
  const asset = SPELL_DICTIONARIES[language];
  return {
    language,
    aff: new URL(asset.aff, base).href,
    dic: new URL(asset.dic, base).href,
  };
}
